import React from 'react';
import type { Grid2Props } from '@mui/material';
import type { Data } from 'framework/types';

export const NEW_ROW_ID_PREFIX = 'new-';
export const EDITABLE_LIST_FIELD = 'field' as const;
export const EDITABLE_LIST_GROUP = 'group' as const;
export const EDITABLE_LIST_NESTED_LIST = 'nested-list' as const;
export const EDITABLE_LIST_COLLAPSIBLE_ROW = 'collapsible-row' as const;
export const EDITABLE_LIST_CUSTOM = 'custom' as const;

export type CellType =
  | typeof EDITABLE_LIST_FIELD
  | typeof EDITABLE_LIST_GROUP
  | typeof EDITABLE_LIST_CUSTOM
  | typeof EDITABLE_LIST_NESTED_LIST
  | typeof EDITABLE_LIST_COLLAPSIBLE_ROW;

interface BasicGridCellInfo {
  type: CellType;
  gridCellProps?: Grid2Props;
}

interface ComponentGridCellInfo<
  T extends React.ComponentType<any>,
  P = React.ComponentProps<T>,
> extends BasicGridCellInfo {
  component: T;
  componentProps?: P;
}

export interface CustomGridCellInfo<T extends React.ComponentType<any>> extends ComponentGridCellInfo<T> {
  type: typeof EDITABLE_LIST_CUSTOM;
}

export interface GroupGridCellInfo<T extends React.ComponentType<any>> extends ComponentGridCellInfo<T> {
  type: typeof EDITABLE_LIST_GROUP;
  children?: GridCellInfo<any>[];
}

export interface NestedListGridCellInfo<T extends React.ComponentType<any>> extends ComponentGridCellInfo<T> {
  type: typeof EDITABLE_LIST_NESTED_LIST;
  source: string;
  rows?: {
    enableUpdateCount?: boolean;
    newRowIdPrefix?: string;
    rowKeySource?: string;
    addNewItemButtonText?: string;
    emptyListComponent?: React.ReactNode;
  };
  children?: GridCellInfo<any>[];
}

export interface CollapsibleRowGridCellInfo extends BasicGridCellInfo {
  type: typeof EDITABLE_LIST_COLLAPSIBLE_ROW;
  title: GridCellInfo<any>[];
  details?: GridCellInfo<any>[];
  defaultExpanded?: boolean;
  expandIcon?: React.ReactNode;
}

export interface FieldGridCellInfo<T extends React.ComponentType<any>> extends ComponentGridCellInfo<
  T,
  Omit<React.ComponentProps<T>, 'source'>
> {
  type: typeof EDITABLE_LIST_FIELD;
  source: string;
  isEditor: boolean;
  isVisible?: (data: Data) => boolean;
}

export type GridCellInfo<T extends React.ComponentType<any>> =
  | GroupGridCellInfo<T>
  | FieldGridCellInfo<T>
  | NestedListGridCellInfo<T>
  | CollapsibleRowGridCellInfo
  | CustomGridCellInfo<T>;

export interface CellProps<T extends GridCellInfo<any>> {
  cellInfo: T;
  rowIndex: number;
  cellIndex: number;
  isRowEnabled: boolean;
}
